import type { MouseEvent } from 'react';
import { analytics } from 'lib/analytics/analytics';
import { homepageCtaProps } from 'components/landing/v3/homepageCta';

/**
 * Attribution for every header link — logo, nav tabs, CTAs, mega-menu and
 * drawer entries. `data-buttontype="header"` is the GTM trigger; the track call
 * is the segment event. The V2 navbar dropped both upstream
 * (HEADER_V2_MIGRATION.md §2 A3), so spread this onto the anchor/button rather
 * than re-deriving it:
 *
 *   <CustomLink href={…} {...headerLinkAttribution({ onClick: close })}>
 *
 * Without `label` the event name uses the element's own text, which is what the
 * pre-V2 header tracked.
 *
 * `buttonType` is main's per-product v3 name (main f42c37e1c). It is delegated
 * to `homepageCtaProps(buttonType, 'header')` rather than reimplemented, so the
 * emitted attributes and the `button_click` dataLayer push are byte-identical
 * to main's `headerNavCtaProps` — including `data-buttontype-legacy="header"`,
 * which is a side effect of that helper and is kept for GTM parity. The segment
 * track call is this branch's own and has no counterpart upstream.
 */
export const headerLinkAttribution = ({
  label,
  onClick,
  buttonType,
}: { label?: string; onClick?: () => void; buttonType?: string } = {}) => {
  const gtm = buttonType ? homepageCtaProps(buttonType, 'header') : null;

  return {
    ...(gtm ?? { 'data-buttontype': 'header' as const }),
    onClick: (e: MouseEvent<HTMLElement>) => {
      gtm?.onClick?.(e);
      const headerLabel = label ?? e.currentTarget.textContent ?? 'link';
      analytics.track(
        'select_content',
        { content_type: 'header', content_id: headerLabel },
        `header - ${headerLabel} - CLICK`,
      );
      onClick?.();
    },
  };
};
